/**
 * «Активность» → «По часам»: как прошёл один день. Спека `docs/specs/2026-11-03-activity-hours-design.md`,
 * числа — те же, что у `prokat-runner activity-hours`. Детализация лежит в том же ответе: число в
 * ячейке всегда равно числу строк списка. Отбор — в адресе (`&hour=17&list=missed`).
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Tile } from "@/components/activity/DaysView";
import {
  type HourCall,
  type HourLead,
  type HourPerson,
  type HourRow,
  type HoursReport,
  type HoursUsual,
  loadActivityHours,
} from "@/lib/activity";

/** Что можно раскрыть в строке часа. */
type Kind =
  | "people"
  | "incoming"
  | "answered"
  | "missed"
  | "short"
  | "nobody"
  | "unreached"
  | "ask"
  | "outgoing"
  | "leads"
  | "need_call"
  | "called"
  | "talked_in"
  | "no_contact"
  | "deals"
  | "zapis";

type Numeric = Exclude<Kind, "people">;

const НАЗВАНИЕ: Record<Kind, string> = {
  people: "По графику и на линии",
  incoming: "Входящие",
  answered: "Отвечено",
  missed: "Пропущено",
  short: "Сбросили за 20 секунд или быстрее",
  nobody: "Никого на линии",
  unreached: "Звонок не дошёл",
  ask: "Дошёл, но не взяли",
  outgoing: "Исходящие",
  leads: "Поступившие лиды",
  need_call: "Лиды, которым нужен звонок",
  called: "Лиды: исходящий был",
  talked_in: "Лиды: поговорили на входящем",
  no_contact: "Лиды без контакта",
  deals: "Сделки",
  zapis: "Записали",
};

const pad = (n: number) => String(n).padStart(2, "0");
const hh = (n: number) => `${pad(n)}:00`;
const range = (r: { start: number; end: number }) => `${hh(r.start)}–${hh(r.end)}`;
const comma = (n: number) => String(n).replace(".", ",");
const pct = (n: number, d: number) => (d > 0 ? Math.round((n * 100) / d) : 0);
const time = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "");
const isKind = (v: string | null): v is Kind => v !== null && v in НАЗВАНИЕ;

/** Ноль — точкой, открывать нечего; будущий час — прочерк. */
function Num({
  r,
  kind,
  onOpen,
  open,
}: {
  r: HourRow;
  kind: Numeric;
  onOpen: (hour: number, kind: Kind) => void;
  open: { hour: number; kind: Kind } | null;
}) {
  if (r.future) return <span className="text-fg-faint">—</span>;
  const n = r[kind];
  if (!n) return <span className="text-fg-faint">·</span>;
  return (
    <button
      type="button"
      aria-expanded={open?.hour === r.start && open.kind === kind}
      aria-label={`${НАЗВАНИЕ[kind]}: ${n}, ${range(r)}, открыть список`}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(r.start, kind);
      }}
      className="text-accent-text underline decoration-border-strong underline-offset-2 hover:decoration-accent-text"
    >
      {n}
    </button>
  );
}

function Отставание({ value, base, today, label }: { value: number; base: number | undefined; today: boolean; label: string }) {
  if (base === undefined) return <>{label} — обычно нет данных: мало истории</>;
  if (today) return <>{label} — обычно {base} за полный день</>;
  const ниже = base > 0 ? Math.round((1 - value / base) * 100) : 0;
  return (
    <>
      {label} — обычно {base}
      {ниже > 0 && (
        <>
          {" · "}
          <span className={ниже > 20 ? "text-warn" : ""}>ниже на {ниже}%</span>
        </>
      )}
    </>
  );
}

function Tiles({ r }: { r: HoursReport }) {
  const t = r.totals;
  const u: HoursUsual | null = r.usual;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-annot="плитки дня">
      <Tile
        label="Входящие"
        value={t.incoming}
        hint={`отвечено ${t.answered} из ${t.incoming} · ${pct(t.answered, t.incoming)}%`}
        extra={<Отставание label="Отвечено" value={t.answered} base={u?.answered} today={r.today} />}
      />
      <Tile
        label="Лиды"
        value={t.leads}
        hint={`нужен звонок ${t.need_call} · без контакта ${t.no_contact}, из них ждут смену ${t.waiting}`}
        extra={<Отставание label="Поступило" value={t.leads} base={u?.leads} today={r.today} />}
      />
      <Tile label="Сделки" value={t.deals} extra={<Отставание label="Сделок" value={t.deals} base={u?.deals} today={r.today} />} />
      <Tile label="Записали" value={t.zapis} extra={<Отставание label="Записали" value={t.zapis} base={u?.zapis} today={r.today} />} />
    </div>
  );
}

function Drops({ r }: { r: HoursReport }) {
  const [открыт, открыть] = useState<number | null>(null);
  return (
    <>
      {r.drops.map((d) => {
        const часы = r.hours.filter((x) => x.start >= d.from && x.end <= d.to);
        const сумма = (к: "nobody" | "unreached" | "ask") => часы.reduce((s, x) => s + x[к], 0);
        const причины = [
          сумма("nobody") && `никого на линии ${сумма("nobody")}`,
          сумма("unreached") && `не дошло ${сумма("unreached")}`,
          сумма("ask") && `не взяли ${сумма("ask")}`,
        ].filter(Boolean);
        return (
          <div key={d.from} className="rounded-[var(--radius-ui)] border border-border bg-warn-bg px-3 py-2.5 text-[12.5px]" data-annot="провал">
            <strong className="font-medium">
              Провал {hh(d.from)}–{hh(d.to)}:
            </strong>{" "}
            {d.incoming} входящих, отвечено {d.answered} ({pct(d.answered, d.incoming)}%).{" "}
            <button
              type="button"
              aria-expanded={открыт === d.from}
              onClick={() => открыть(открыт === d.from ? null : d.from)}
              className="text-accent-text underline decoration-border-strong underline-offset-2 hover:decoration-accent-text"
            >
              По графику {comma(d.plan)}, на линии {comma(d.line_min)}–{comma(d.line_max)}
            </button>
            .{причины.length > 0 && ` Пропуски: ${причины.join(", ")}.`}
            {открыт === d.from && (
              <div className="mt-2 rounded-[var(--radius-ui)] border border-border bg-bg p-2">
                <People rows={d.people} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

const ОПРЕДЕЛЕНИЯ: [string, string][] = [
  ["Входящие", "Отдельные вызовы по времени начала, не номера: клиент может набрать несколько раз."],
  ["Пропущено", "Все непринятые входящие. Подписи внизу — признаки внутри пропущенных, складывать их нельзя: сброс за 20 секунд может совпасть с «не дошло»."],
  ["Никого на линии", "Пока шёл звонок, никто из контакт-центра не стоял в статусе «на линии»."],
  ["Не дошло", "Сотрудник стоял на линии и был свободен, но его телефон и вкладка Bitrix не ответили. Статус «на линии» ещё не значит, что человек может взять звонок. Видно по логу звонка, он разбирается только в 10–21."],
  ["Не взяли", "Звонок дошёл до свободного сотрудника, дозванивались от 10 секунд, трубку не сняли."],
  ["Нужен звонок", "Лид не спам, не дубль и не вакансия, номер настоящий."],
  ["Исходящий был", "Наш звонок на номер лида до срока. Разговор не обязателен: важна попытка."],
  ["Поговорили на входящем", "Исходящего нет, но клиент дозвонился сам и мы ответили. Такой лид обработан: половина лидов создаётся из входящего звонка."],
  ["Без контакта", "Ни исходящего, ни разговора на входящем до срока. Это настоящая потеря."],
  ["Срок", "21:00 дня создания лида. Лид после 21:00 звонится на следующий день: до 21:00 завтра он «ждёт смену», а не потерян."],
  ["По графику", "Смены сотрудников контакт-центра из графика CRM. Кто ушёл раньше, остаётся в графике до конца смены: график не знает про уход. Замены в графике не отмечены."],
  ["На линии", "Статусы CRM, в которых сотрудник принимает вызовы: «Работаю» и «Задача (В очереди)». Считаются только статусы, поставленные в этот день; «Ушел», «Перерыв» и «Задача (Без очереди)» — не на линии. Число — среднее за час: человеко-минуты / 60, 1,4 — это 84 минуты на всех. Статус — слова сотрудника: телефон при нём может быть не в сети, это видно в «не дошло»."],
  ["Сделки и записи", "События этого часа. Они могут относиться к старым лидам, поэтому делить их на лиды часа нельзя. Сделки — только воронка контакт-центра."],
  ["Обычно", "Среднее по четырём прошлым таким же дням недели."],
  ["Провал", "Подряд идущие завершённые часы 10–21, где отвечено меньше 60% при 5 и более входящих. Соседний час с меньшим числом входящих присоединяется, если и там ответов меньше 60%."],
];

function Definitions() {
  return (
    <details className="text-[12px]" data-annot="как считаем">
      <summary className="w-fit cursor-pointer text-accent-text">Как считаем звонки, лиды и людей</summary>
      <dl className="mt-2 grid gap-x-3 gap-y-1.5 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-3 sm:grid-cols-[170px_1fr]">
        {ОПРЕДЕЛЕНИЯ.map(([d, t]) => (
          <Fragment key={d}>
            <dt className="font-medium">{d}</dt>
            <dd className="m-0 text-fg-muted">{t}</dd>
          </Fragment>
        ))}
      </dl>
    </details>
  );
}

function Key({ cls, text }: { cls: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <i className={`inline-block size-2.5 ${cls}`} />
      {text}
    </span>
  );
}

/** Столбик: части снизу вверх, высота — абсолютная нагрузка часа. */
function Bar({ parts, max }: { parts: [string, number][]; max: number }) {
  const целое = parts.reduce((s, [, v]) => s + v, 0);
  return (
    <div className="flex h-[76px] items-end justify-center border-b border-border-strong">
      <div className="flex w-[65%] min-w-3.5 flex-col-reverse" style={{ height: `${(целое * 100) / Math.max(1, max)}%` }}>
        {parts.map(([cls, v]) => (v ? <span key={cls} className={`block ${cls}`} style={{ height: `${(v * 100) / целое}%` }} /> : null))}
      </div>
    </div>
  );
}

const ЗВОНКИ = "bg-ok";
const КОЛОНКА = (r: HourRow) =>
  `min-w-0 cursor-pointer px-0.5 text-center disabled:cursor-default ${r.start >= 10 && r.start < 21 ? "bg-bg" : ""} ${
    r.failure ? "outline outline-1 -outline-offset-1 outline-warn" : ""
  } ${r.future ? "opacity-45" : ""}`;

function Graphs({ rows, onOpen }: { rows: HourRow[]; onOpen: (hour: number, kind: Kind) => void }) {
  const часы = rows.filter((r) => r.start >= 8 && r.start <= 21);
  const maxCalls = Math.max(1, ...часы.map((r) => r.incoming));
  const maxLeads = Math.max(1, ...часы.map((r) => r.leads));
  const сетка = "grid min-w-[700px] grid-cols-[repeat(14,minmax(0,1fr))] gap-1";
  const подпись = "h-[18px] text-[10px] text-fg-muted";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="графики по часам">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-3 py-2.5">
        <h2 className="text-[13px] font-medium">Нагрузка и обработка</h2>
        <span className="text-[11.5px] text-fg-muted">08–21 по часам · ночные интервалы — в таблице</span>
      </div>
      <div className="overflow-x-auto px-3 py-2.5">
        <div className="mb-1 min-w-[700px] text-[12px]">
          <b className="font-medium">Входящие</b> <span className="text-[11px] text-fg-muted">шкала 0–{maxCalls} вызовов</span>
        </div>
        <div className={сетка}>
          {часы.map((r) => (
            <button
              key={r.start}
              type="button"
              disabled={r.future || !r.incoming}
              onClick={() => onOpen(r.start, "incoming")}
              aria-label={`${range(r)}: входящих ${r.incoming}, отвечено ${r.answered}, открыть список`}
              className={КОЛОНКА(r)}
            >
              <div className={подпись}>{r.future ? "—" : r.incoming}</div>
              <Bar max={maxCalls} parts={[[ЗВОНКИ, r.answered], ["bg-err", r.missed - r.short], ["bg-warn", r.short]]} />
              <div className={`${подпись} whitespace-nowrap`}>{r.incoming ? `${pct(r.answered, r.incoming)}% отв.` : "·"}</div>
              <div className="pb-0.5 text-[11px] tabular-nums">{pad(r.start)}</div>
            </button>
          ))}
        </div>
        <div className="mb-3 mt-1.5 flex min-w-[700px] flex-wrap gap-3 text-[11px] text-fg-muted">
          <Key cls={ЗВОНКИ} text="Отвечено" />
          <Key cls="bg-err" text="Пропущено > 20 с" />
          <Key cls="bg-warn" text="Сбросили ≤ 20 с" />
        </div>
        <div className="mb-1 min-w-[700px] text-[12px]">
          <b className="font-medium">Лиды</b> <span className="text-[11px] text-fg-muted">шкала 0–{maxLeads} лидов, своя</span>
        </div>
        <div className={сетка}>
          {часы.map((r) => (
            <button
              key={r.start}
              type="button"
              disabled={r.future || !r.leads}
              onClick={() => onOpen(r.start, "leads")}
              aria-label={`${range(r)}: лидов ${r.leads}, без контакта ${r.no_contact}, открыть список`}
              className={КОЛОНКА(r)}
            >
              <div className={подпись}>{r.future ? "—" : r.leads}</div>
              <Bar
                max={maxLeads}
                parts={[
                  ["bg-accent", r.called],
                  [ЗВОНКИ, r.talked_in],
                  ["bg-err", r.overdue],
                  ["bg-border-strong", r.waiting],
                  ["bg-bg-hover", r.leads - r.need_call],
                ]}
              />
              <div className={`${подпись} whitespace-nowrap`}>
                {r.overdue ? `без конт. ${r.overdue}` : r.waiting ? `ждут ${r.waiting}` : "·"}
              </div>
              <div className="pb-0.5 text-[11px] tabular-nums">{pad(r.start)}</div>
            </button>
          ))}
        </div>
        <div className="mb-3 mt-1.5 flex min-w-[700px] flex-wrap gap-3 text-[11px] text-fg-muted">
          <Key cls="bg-accent" text="Исходящий был" />
          <Key cls={ЗВОНКИ} text="Поговорили на входящем" />
          <Key cls="bg-err" text="Без контакта" />
          <Key cls="bg-border-strong" text="Ждут смену" />
          <Key cls="bg-bg-hover" text="Не нужен звонок" />
        </div>
        <div className="mb-1 min-w-[700px] text-[12px]">
          <b className="font-medium">Люди на линии / по графику</b>{" "}
          <span className="text-[11px] text-fg-muted">на линии — среднее за час, клик: кто по графику и кто по статусу</span>
        </div>
        <div className={сетка}>
          {часы.map((r) => {
            const мало = !r.future && r.line < r.plan;
            return (
              <button
                key={r.start}
                type="button"
                disabled={r.future}
                onClick={() => onOpen(r.start, "people")}
                title={r.people.map((p) => p.name).join(", ") || "никого"}
                className={`min-w-0 rounded-[var(--radius-ui)] border border-border px-px py-1 text-center text-[11px] tabular-nums ${мало ? "bg-warn-bg" : "bg-bg"} ${r.future ? "opacity-45" : ""}`}
              >
                <div>{pad(r.start)}</div>
                <b className="font-medium">
                  {r.future ? "—" : comma(r.line)} / {comma(r.plan)}
                </b>
                <span className="block whitespace-nowrap text-[10px] text-fg-muted">
                  {r.future ? "позже" : мало ? "меньше плана" : "по плану"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const КЛЕТКА = "border-b border-border px-1.5 py-1 text-left";

function Шапка({ cols }: { cols: string[] }) {
  return (
    <thead className="text-fg-muted">
      <tr>
        {cols.map((c) => (
          <th key={c} className={`${КЛЕТКА} font-normal`}>
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

const ВИД: Record<string, string> = {
  ask: "не взял",
  unreached: "не дошло",
  busy: "в своём звонке",
  short: "звонили < 10 с",
  wait: "ждал в очереди",
};

function Calls({ rows }: { rows: HourCall[] }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <Шапка cols={["Время", "Номер", "Ждал, с", "Что случилось", "Кто был", "Перезвон"]} />
      <tbody>
        {rows.map((c) => (
          <tr key={c.id}>
            <td className={КЛЕТКА}>{c.at}</td>
            <td className={КЛЕТКА}>
              <Link to={`/calls/phone/${c.phone}`} className="text-accent-text hover:underline">
                {c.phone}
              </Link>
            </td>
            <td className={КЛЕТКА}>{c.wait}</td>
            <td className={КЛЕТКА}>
              {c.answered ? "ответили" : c.word}
              {c.short && <span className="text-fg-faint"> · сбросил ≤ 20 с</span>}
            </td>
            <td className={КЛЕТКА}>
              {c.who.length ? c.who.map((w) => (w.kind ? `${w.name} — ${ВИД[w.kind] ?? w.kind}` : w.name)).join(", ") : "—"}
            </td>
            <td className={КЛЕТКА}>
              {c.answered ? "—" : c.callback_at ? `разговор ${time(c.callback_at)}` : c.tried ? "звонили, разговора нет" : "не перезвонили"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ИТОГ_ЛИДА: Record<HourLead["group"], string> = {
  called: "исходящий был",
  talked_in: "поговорили на входящем",
  no_contact: "без контакта",
  skip: "звонок не нужен",
};

function Leads({ rows }: { rows: HourLead[] }) {
  return (
    <table className="w-full border-collapse text-[12px]">
      <Шапка cols={["Время", "Лид", "Номер", "Источник", "Статус", "Итог", "Срок"]} />
      <tbody>
        {rows.map((l) => (
          <tr key={l.id}>
            <td className={КЛЕТКА}>{l.at}</td>
            <td className={КЛЕТКА}>
              <Link to={`/leads/${l.id}`} className="text-accent-text hover:underline">
                {l.id}
              </Link>
            </td>
            <td className={КЛЕТКА}>{l.phone}</td>
            <td className={КЛЕТКА}>{l.source}</td>
            <td className={КЛЕТКА}>{l.status}</td>
            <td className={КЛЕТКА}>
              {l.waiting ? "ждёт смену" : ИТОГ_ЛИДА[l.group]}
              {l.late_at && <span className="text-fg-faint"> · позвонили позже: {time(l.late_at)}</span>}
            </td>
            <td className={КЛЕТКА}>{l.group === "skip" ? "—" : time(l.deadline)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Люди отрезка: график и статусы по человеку, чтобы «по графику 2, на линии 0» читалось по именам. */
function People({ rows }: { rows: HourPerson[] }) {
  if (!rows.length) return <p className="m-0 text-fg-muted">Никого не было ни по графику, ни на линии.</p>;
  const отрезки = (x: string[][]) => x.map(([a, b]) => `${a}–${b}`).join(", ");
  return (
    <table className="w-full border-collapse text-[12px]">
      <Шапка cols={["Сотрудник", "По графику", "Статусы", "На линии", "Звонков", "Сообщений"]} />
      <tbody>
        {rows.map((p) => (
          <tr key={p.name}>
            <td className={КЛЕТКА}>
              {p.name}
              {p.note && <span className="block text-[11px] text-warn">{p.note}</span>}
            </td>
            <td className={КЛЕТКА}>{отрезки(p.plan) || <span className="text-fg-faint">нет в графике</span>}</td>
            <td className={КЛЕТКА}>
              {p.statuses.length ? p.statuses.map(([имя, a, b]) => `${имя} ${a}–${b}`).join(", ") : <span className="text-fg-faint">нет</span>}
            </td>
            <td className={КЛЕТКА}>{отрезки(p.spans) || <span className="text-fg-faint">не на линии</span>}</td>
            <td className={КЛЕТКА}>{p.calls}</td>
            <td className={КЛЕТКА}>{p.sent}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Detail({ r, kind, onClose, colSpan }: { r: HourRow; kind: Kind; onClose: () => void; colSpan: number }) {
  const ref = useRef<HTMLDivElement>(null);
  // Фигурные скобки обязательны: `scrollIntoView` в свежем Chromium отдаёт Promise, а что вернул
  // эффект, React считает очисткой и вызывает как функцию («l is not a function» при закрытии).
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "nearest" });
  }, []);
  const вызовы: Partial<Record<Kind, HourCall[]>> = {
    incoming: r.calls,
    answered: r.calls.filter((c) => c.answered),
    missed: r.calls.filter((c) => !c.answered),
    short: r.calls.filter((c) => c.short),
    nobody: r.calls.filter((c) => c.reason === "nobody"),
    unreached: r.calls.filter((c) => c.reason === "unreached"),
    ask: r.calls.filter((c) => c.reason === "ask"),
  };
  const лиды: Partial<Record<Kind, HourLead[]>> = {
    leads: r.lead_rows,
    need_call: r.lead_rows.filter((l) => l.group !== "skip"),
    called: r.lead_rows.filter((l) => l.group === "called"),
    talked_in: r.lead_rows.filter((l) => l.group === "talked_in"),
    no_contact: r.lead_rows.filter((l) => l.group === "no_contact"),
  };
  const сделки = kind === "deals" ? r.deal_rows : kind === "zapis" ? r.zapis_rows : null;
  const счёт = kind === "people" ? r.people.length : kind === "outgoing" ? r.out_calls.length : (вызовы[kind] ?? лиды[kind] ?? сделки ?? []).length;
  let тело: ReactNode;
  if (kind === "people") {
    тело = <People rows={r.people} />;
  } else if (вызовы[kind]) {
    тело = <Calls rows={вызовы[kind]} />;
  } else if (лиды[kind]) {
    тело = <Leads rows={лиды[kind]} />;
  } else if (сделки) {
    тело = (
      <table className="w-full border-collapse text-[12px]">
        <Шапка cols={["Время", "Сделка"]} />
        <tbody>
          {сделки.map((d) => (
            <tr key={d.id}>
              <td className={КЛЕТКА}>{d.at}</td>
              <td className={КЛЕТКА}>
                <Link to={`/deals/${d.id}`} className="text-accent-text hover:underline">
                  {d.title}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    тело = (
      <table className="w-full border-collapse text-[12px]">
        <Шапка cols={["Время", "Номер", "Дозвон"]} />
        <tbody>
          {r.out_calls.map((c) => (
            <tr key={c.id}>
              <td className={КЛЕТКА}>{c.at}</td>
              <td className={КЛЕТКА}>
                <Link to={`/calls/phone/${c.phone}`} className="text-accent-text hover:underline">
                  {c.phone}
                </Link>
              </td>
              <td className={КЛЕТКА}>{c.answered ? "соединились" : "нет"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <tr>
      <td colSpan={colSpan} className="border-y border-border-strong bg-bg p-3 text-left">
        <div ref={ref} className="sticky left-3 max-w-[calc(100vw-4rem)]">
          <div className="mb-2 flex items-center justify-between gap-3">
            <strong className="font-medium">
              {НАЗВАНИЕ[kind]} · {range(r)} · {счёт}
            </strong>
            <button type="button" onClick={onClose} className="h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] hover:bg-bg-hover">
              Свернуть
            </button>
          </div>
          {тело}
        </div>
      </td>
    </tr>
  );
}

const КОЛОНКИ: { group: string; items: { kind: Numeric | "plan" | "line"; head: string; hint: string }[] }[] = [
  {
    group: "Люди",
    items: [
      { kind: "plan", head: "по графику", hint: "сколько человек стояло в графике; клик — кто" },
      { kind: "line", head: "на линии", hint: "среднее за час: человеко-минуты в статусах «Работаю» и «Задача (В очереди)» / 60; клик — кто" },
    ],
  },
  {
    group: "Входящие",
    items: [
      { kind: "incoming", head: "поступило", hint: "вызовы, не номера" },
      { kind: "answered", head: "отвечено", hint: "приняты оператором" },
      { kind: "missed", head: "пропущено", hint: "все непринятые; подписи — почему, складывать их нельзя" },
    ],
  },
  { group: "Исходящие", items: [{ kind: "outgoing", head: "всего", hint: "все исходящие этого часа" }] },
  {
    group: "Лиды",
    items: [
      { kind: "leads", head: "поступило", hint: "лиды по времени создания" },
      { kind: "need_call", head: "нужен звонок", hint: "без спама, дублей, вакансий и лидов без номера" },
      { kind: "called", head: "исходящий был", hint: "наш звонок до срока" },
      { kind: "talked_in", head: "на входящем", hint: "исходящего нет, но поговорили на входящем" },
      { kind: "no_contact", head: "без контакта", hint: "ни исходящего, ни разговора до срока" },
    ],
  },
  {
    group: "Итог",
    items: [
      { kind: "deals", head: "сделок", hint: "созданные в этот час сделки воронки контакт-центра" },
      { kind: "zapis", head: "записали", hint: "вошли в «Ждем клиента» в этот час" },
    ],
  },
];

/** «По графику» и «на линии» — одна кнопка к одному списку: в нём обе цифры разложены по людям. */
function PeopleNum({
  x,
  kind,
  open,
  onOpen,
}: {
  x: HourRow;
  kind: "plan" | "line";
  open: { hour: number; kind: Kind } | null;
  onOpen: (hour: number, kind: Kind) => void;
}) {
  if (kind === "line" && x.future) return <span className="text-fg-faint">—</span>;
  const n = x[kind];
  if (!n && !x.people.length) return <span className="text-fg-faint">·</span>;
  return (
    <button
      type="button"
      aria-expanded={open?.hour === x.start && open.kind === "people"}
      aria-label={`${kind === "plan" ? "По графику" : "На линии"}: ${comma(n)}, ${range(x)}, кто`}
      onClick={() => onOpen(x.start, "people")}
      className={`underline decoration-border-strong underline-offset-2 hover:decoration-accent-text ${
        kind === "line" && x.line < x.plan ? "text-warn" : "text-accent-text"
      }`}
    >
      {n ? comma(n) : "·"}
    </button>
  );
}

const ЯЧЕЙКИ = КОЛОНКИ.flatMap((g) => g.items.map((c, i) => ({ ...c, first: i === 0 })));

function Hours({
  rows,
  r,
  open,
  onOpen,
  onClose,
}: {
  rows: HourRow[];
  r: HoursReport;
  open: { hour: number; kind: Kind } | null;
  onOpen: (hour: number, kind: Kind) => void;
  onClose: () => void;
}) {
  const t = r.totals;
  const Подписи = ({ x }: { x: HourRow }) => {
    const части = (
      [
        ["≤20 с", "short"],
        ["без линии", "nobody"],
        ["не дошло", "unreached"],
        ["не взяли", "ask"],
      ] as const
    ).filter(([, к]) => x[к] > 0);
    return (
      <span className="block whitespace-nowrap text-[10px] text-fg-muted">
        {части.map(([слово, к], i) => (
          <Fragment key={к}>
            {i > 0 && " · "}
            {слово} <Num r={x} kind={к} onOpen={onOpen} open={open} />
          </Fragment>
        ))}
      </span>
    );
  };
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="таблица по часам">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] border-collapse text-[12.5px] tabular-nums">
          <thead className="text-[11.5px] text-fg-muted">
            <tr>
              <th rowSpan={2} className="sticky left-0 z-10 bg-bg-subtle px-3 py-2 text-left font-normal">
                Час, МСК
              </th>
              {КОЛОНКИ.map((g) => (
                <th key={g.group} colSpan={g.items.length} className="border-l border-border px-1.5 pt-2 text-center font-medium">
                  {g.group}
                </th>
              ))}
            </tr>
            <tr className="border-b border-border">
              {ЯЧЕЙКИ.map((c) => (
                <th key={c.kind} title={c.hint} className={`cursor-help px-1.5 py-1.5 text-right font-normal leading-tight ${c.first ? "border-l border-border" : ""}`}>
                  {c.head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => {
              const раскрыта = open?.hour === x.start;
              const фон = x.failure ? "bg-warn-bg" : x.start >= 10 && x.start < 21 ? "bg-bg" : "bg-bg-subtle";
              return (
                <Fragment key={x.start}>
                  <tr className={`border-t border-border ${фон} ${x.future ? "opacity-45" : ""}`} data-annot={`час ${pad(x.start)}`}>
                    <th scope="row" className={`sticky left-0 z-10 min-w-[104px] px-3 py-1.5 text-left font-normal ${фон}`}>
                      <button
                        type="button"
                        disabled={x.future}
                        aria-expanded={раскрыта && open?.kind === "people"}
                        onClick={() => onOpen(x.start, "people")}
                        className="flex items-center gap-1 whitespace-nowrap font-medium"
                      >
                        {раскрыта ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        {x.end - x.start > 1 ? `${pad(x.start)}–${pad(x.end)}` : hh(x.start)}
                      </button>
                      {x.failure && <span className="block text-[10px]">Провал · {pct(x.answered, x.incoming)}% ответов</span>}
                      {x.partial && <span className="block text-[10px] text-fg-muted">час ещё идёт</span>}
                      {x.future && <span className="block text-[10px] text-fg-muted">ещё не наступил</span>}
                    </th>
                    {ЯЧЕЙКИ.map((c) => (
                      <td key={c.kind} className={`px-1.5 py-1.5 text-right align-middle ${c.first ? "border-l border-border" : ""}`}>
                        {c.kind === "plan" || c.kind === "line" ? (
                          <PeopleNum x={x} kind={c.kind} open={open} onOpen={onOpen} />
                        ) : (
                          <Num r={x} kind={c.kind} onOpen={onOpen} open={open} />
                        )}
                        {c.kind === "answered" && !x.future && x.incoming > 0 && (
                          <span className="block text-[10px] text-fg-muted">{pct(x.answered, x.incoming)}%</span>
                        )}
                        {c.kind === "missed" && !x.future && x.missed > 0 && <Подписи x={x} />}
                        {c.kind === "no_contact" && !x.future && x.no_contact > 0 && (
                          <span className="block whitespace-nowrap text-[10px] text-fg-muted">
                            {x.overdue > 0 && `просрочено ${x.overdue}`}
                            {x.overdue > 0 && x.waiting > 0 && " · "}
                            {x.waiting > 0 && `ждут смену ${x.waiting}`}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                  {раскрыта && open && <Detail r={x} kind={open.kind} onClose={onClose} colSpan={ЯЧЕЙКИ.length + 1} />}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t border-border-strong font-medium">
            <tr>
              <td className="sticky left-0 z-10 bg-bg-subtle px-3 py-2">{r.today ? `Итого к ${r.now.slice(11, 16)}` : "Итого"}</td>
              {ЯЧЕЙКИ.map((c) => (
                <td key={c.kind} className={`px-1.5 py-2 text-right ${c.first ? "border-l border-border" : ""}`}>
                  {c.kind === "plan" || c.kind === "line" ? "" : t[c.kind] || <span className="font-normal text-fg-faint">·</span>}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

export function HoursView({ day }: { day: string }) {
  const [params, setParams] = useSearchParams();
  const report = useQuery({
    queryKey: ["activity-hours", day],
    queryFn: () => loadActivityHours({ date: day }),
    placeholderData: keepPreviousData,
  });
  const hourRaw = params.get("hour");
  const kindRaw = params.get("list");
  const open = hourRaw !== null && isKind(kindRaw) ? { hour: Number(hourRaw), kind: kindRaw } : null;

  const отбор = (next: { hour: number; kind: Kind } | null) => {
    const out = new URLSearchParams(params);
    out.delete("hour");
    out.delete("list");
    if (next) {
      out.set("hour", String(next.hour));
      out.set("list", next.kind);
    }
    setParams(out, { replace: true });
  };
  const открыть = (hour: number, kind: Kind) => отбор(open?.hour === hour && open.kind === kind ? null : { hour, kind });

  if (report.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю…</div>;
  if (report.isError) return <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(report.error.message)}</div>;
  const r = report.data;
  const t = r.totals;
  if (!t.incoming && !t.leads && !t.outgoing && !t.deals && !r.today) {
    return <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[12.5px] text-fg-muted">За этот день данных нет.</div>;
  }
  return (
    <div className={`flex flex-col gap-3 ${report.isPlaceholderData ? "opacity-60" : ""}`}>
      <Tiles r={r} />
      <Drops r={r} />
      {r.today && <p className="m-0 text-[12px] text-fg-muted">День ещё идёт: данные к {r.now.slice(11, 16)} МСК, срок обработки лидов не вышел.</p>}
      <Definitions />
      <Graphs rows={r.hours} onOpen={открыть} />
      <Hours rows={r.hours} r={r} open={open} onOpen={открыть} onClose={() => отбор(null)} />
      <div className="text-[12px] text-fg-faint">
        МСК · 10:00–21:00 — рабочее время. Цифра открывает записи внутри строки; час, «по графику» и «на линии» — кто был по графику и по статусу. Ноль — «·», будущее — «—».
      </div>
    </div>
  );
}
